import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X, Phone } from "lucide-react";
import Logo from "./Logo";
import LanguageSwitcher from "./LanguageSwitcher";
import { useI18n } from "../i18n/I18nContext";

export default function Navbar() {
  const { t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(false);
  const location = useLocation();

  const NAV = [
    { label: t("nav.offer"), to: "/stock" },
    { label: t("nav.upcoming"), to: "/binnenkort" },
    {
      label: t("nav.services"),
      children: [
        { label: t("nav.financing"), to: "/financiering" },
        { label: t("nav.aftersales"), to: "/naverkoop-personalisatie" },
        { label: t("nav.tradeIn"), to: "/overname" },
        { label: t("nav.sellMyCar"), to: "/verkoop-mijn-auto" },
        { label: t("nav.buyYourCar"), to: "/wij-kopen-uw-auto" },
      ],
    },
    { label: t("nav.sold"), to: "/verkocht" },
    { label: t("nav.about"), to: "/over-ons" },
    { label: t("nav.contact"), to: "/contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled || mobileOpen
          ? "bg-[#1a1a1a]/95 backdrop-blur-md border-b border-[#c5a76f]/15"
          : "bg-gradient-to-b from-black/70 to-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 h-[88px] flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setOpenDropdown(true)}
                onMouseLeave={() => setOpenDropdown(false)}
              >
                <button className="flex items-center gap-1 px-4 py-2 text-[#c5a76f] hover:text-[#d8bd86] text-sm font-medium tracking-wide transition-colors">
                  {item.label}
                  <ChevronDown className="w-4 h-4" />
                </button>
                {openDropdown && (
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-2">
                    <div className="bg-[#242424] border border-[#c5a76f]/25 rounded-md shadow-xl min-w-[240px] py-2">
                      {item.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          className="block px-5 py-2.5 text-sm text-white/85 hover:text-[#c5a76f] hover:bg-white/[0.03] transition-colors"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-medium tracking-wide transition-colors ${
                    isActive ? "text-[#d8bd86]" : "text-[#c5a76f] hover:text-[#d8bd86]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="tel:+32471866784"
            className="flex items-center gap-2 text-[#c5a76f] hover:text-[#d8bd86] transition-colors text-sm"
          >
            <Phone className="w-4 h-4" />
            +32 471 86 67 84
          </a>
          <LanguageSwitcher />
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <LanguageSwitcher compact />
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="text-[#c5a76f] p-2"
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-[#c5a76f]/15 bg-[#1a1a1a]">
          <div className="px-5 py-4 flex flex-col">
            {NAV.map((item) =>
              item.children ? (
                <div key={item.label} className="py-1">
                  <div className="text-[#c5a76f] text-sm font-semibold py-2">
                    {item.label}
                  </div>
                  {item.children.map((c) => (
                    <Link
                      key={c.to}
                      to={c.to}
                      className="block pl-4 py-2 text-white/80 text-sm hover:text-[#c5a76f]"
                    >
                      {c.label}
                    </Link>
                  ))}
                </div>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  className="block py-3 text-[#c5a76f] text-sm font-medium border-b border-white/5"
                >
                  {item.label}
                </Link>
              )
            )}
            <a
              href="tel:+32471866784"
              className="mt-4 text-[#c5a76f] text-sm flex items-center gap-2"
            >
              <Phone className="w-4 h-4" /> +32 471 86 67 84
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
